import React, { useState, useEffect ,useCallback} from 'react';
import {
  Box, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  Text, Button, IconButton, HStack, VStack, Flex,
  useColorModeValue, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalFooter, ModalBody, ModalCloseButton, FormControl, FormLabel,
  Input, Textarea, useDisclosure, useToast, InputGroup,
  InputLeftElement, Card, CardBody, Stat, StatLabel, StatNumber,
  SimpleGrid, Menu, MenuButton, MenuList, MenuItem, Spinner, Center,
  Alert, AlertIcon, AlertTitle, AlertDescription, Tooltip, Tag, TagLabel, Select
} from '@chakra-ui/react';
import {
  MdSearch, MdRefresh, MdAdd, MdMoreVert, MdEdit, MdDelete,
  MdVisibility, MdCode,
} from 'react-icons/md';
import { TbChecklist } from 'react-icons/tb';
import apiService from '../services/apiService';
import { useNavigate } from 'react-router-dom';

import SqlEditor from '../components/Common/SQLEditor';
import usageTracker from '../services/usageTracker';
// ─────────────────────────────────────────────────────────────────────────────

const ChecklistManager = ({ userProfile, appConfig, authServiceReady }) => {

  // ── Color mode tokens ────────────────────────────────────────────────────
  const bgColor           = useColorModeValue('gray.50',   'gray.900');
  const cardBg            = useColorModeValue('white',     'gray.800');
  const borderColor       = useColorModeValue('gray.200',  'gray.700');
  const textColor         = useColorModeValue('gray.600',  'gray.300');
  const headingColor      = useColorModeValue('gray.800',  'white');
  const mutedTextColor    = useColorModeValue('gray.500',  'gray.400');
  const inputBg           = useColorModeValue('white',     'gray.700');
  const hoverBg           = useColorModeValue('gray.50',   'gray.700');
  const tableHeaderBg     = useColorModeValue('gray.50',   'gray.900');
  const statsCardBg       = useColorModeValue('white',     'gray.800');
  const btnHoverBorder    = useColorModeValue('gray.300',  'gray.600');
  const purpleHoverBg     = useColorModeValue('purple.50', 'purple.900');
  const purpleBorder      = useColorModeValue('purple.200','purple.600');
  const purpleText        = useColorModeValue('purple.600','purple.300');
  const purpleHoverBorder = useColorModeValue('purple.300','purple.500');
  const blueHover         = useColorModeValue('blue.600',  'blue.500');
  const modalOverlay      = useColorModeValue('blackAlpha.300', 'blackAlpha.700');
  const menuShadow        = useColorModeValue('lg',        'dark-lg');
  const redHoverBg        = useColorModeValue('red.50',    'red.900');
  const searchIconColor   = useColorModeValue('gray.400',  'gray.500');
  const tagSqlBg          = useColorModeValue('blue.50',   'blue.900');
  const tagSqlColor       = useColorModeValue('blue.600',  'blue.200');
  const descBg            = useColorModeValue('gray.50',   'gray.700');
  const metaBg            = useColorModeValue('gray.50',   'gray.700');

  // SQL editor specific tokens — passed as a stable object to SqlEditor
  const sqlEditorBg    = useColorModeValue('gray.900',  'gray.950');
  const sqlEditorBorder= useColorModeValue('gray.600',  'gray.700');
  const sqlEditorHdrBg = useColorModeValue('gray.800',  'gray.900');
  const sqlEditorHdrBd = useColorModeValue('gray.600',  'gray.700');
  const sqlTagBg       = useColorModeValue('blue.600',  'blue.500');
  const sqlCharColor   = useColorModeValue('gray.400',  'gray.500');
  const sqlPlaceholder = useColorModeValue('gray.600',  'gray.600');
  const viewSqlBodyBg  = useColorModeValue('gray.900',  'gray.950');
  const copyBtnBg      = useColorModeValue('gray.700',  'gray.600');
  const copyBtnHoverBg = useColorModeValue('gray.600',  'gray.500');

  // Stable token bundle passed to SqlEditor
  const sqlTokens = {
    sqlEditorBg, sqlEditorBorder, sqlEditorHdrBg, sqlEditorHdrBd,
    sqlTagBg, sqlCharColor, sqlPlaceholder, viewSqlBodyBg, copyBtnBg, copyBtnHoverBg,
  };

  const { isOpen, onOpen, onClose }                                      = useDisclosure();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const toast = useToast();

  const [checklists,  setChecklists]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [searchTerm,  setSearchTerm]  = useState('');
  const [pageSize,    setPageSize]    = useState('20 per page');
  const [currentPage, setCurrentPage] = useState(1);
  const [isEditMode,  setIsEditMode]  = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);

  const emptyForm = { name: '', description: '', checkListQuery: '' };
  const [formData,   setFormData]   = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete,    setItemToDelete]    = useState(null);

  // ─── Fetch ────────────────────────────────────────────────────────────────
  const fetchChecklists = async () => {
    try {
      setLoading(true); setError(null);
      const res = await apiService.fetchChecklists();
      if (res.success && res.data) setChecklists(Array.isArray(res.data) ? res.data : []);
      else throw new Error(res.error || res.message || 'Failed to fetch checklists');
    } catch (err) {
      console.error(err);
      setChecklists([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchChecklists(); }, []);

  // ─── Stats ────────────────────────────────────────────────────────────────
  const stats = {
    total:     checklists.length,
    withQuery: checklists.filter(c => c.checkListQuery?.trim()).length,
    recent:    checklists.filter(c => {
      if (!c.updated_at) return false;
      return (Date.now() - new Date(c.updated_at)) < 7 * 24 * 60 * 60 * 1000;
    }).length,
  };

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const handleSearch  = val => { setSearchTerm(val); setCurrentPage(1); };
  const resetFilters  = () => { setSearchTerm(''); setCurrentPage(1); };
  const handleRefresh = () => {
    fetchChecklists();
    toast({ title: 'Refreshed', status: 'info', duration: 2000, isClosable: true });
  };
    const navigate = useNavigate();

  const handleCreateNew  = () => { setIsEditMode(false); setEditingItem(null); setFormData(emptyForm); setFormErrors({}); onOpen(); };
  const handleModalClose = () => { setIsEditMode(false); setEditingItem(null); setFormData(emptyForm); setFormErrors({}); onClose(); };

  const validate = () => {
    const e = {};
    if (!formData.name?.trim())           e.name           = 'Name is required.';
    if (!formData.checkListQuery?.trim()) e.checkListQuery = 'SQL query is required.';
    return e;
  };

  // ─── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) { setFormErrors(e); return; }
    const payload = {
      name:           formData.name.trim(),
      description:    formData.description.trim(),
      checkListQuery: formData.checkListQuery.trim(),
      updatedBy:      userProfile.email,
    };
    try {
      if (isEditMode) {
        payload.id = editingItem.id;
        const res = await apiService.updateChecklist(payload);
        if (res.success) {
            usageTracker.action('Checklist', 'UPDATE');
          handleRefresh(); handleModalClose();
          toast({ title: 'Checklist Updated', description: `"${payload.name}" updated.`, status: 'success', duration: 3000, isClosable: true });
        } else throw new Error(res.error || res.message || 'Failed to update');
      } else {
        const res = await apiService.createCheckList(payload);
        if (res.success) {
            usageTracker.action('Checklist', 'CREATE');
          handleRefresh(); handleModalClose();
          toast({ title: 'Checklist Created', description: `"${payload.name}" created.`, status: 'success', duration: 3000, isClosable: true });
        } else throw new Error(res.error || res.message || 'Failed to create');
      }
    } catch (err) {
      console.error(err);
      toast({ title: 'Error', description: err.message || 'Operation failed.', status: 'error', duration: 3000, isClosable: true });
    }
  };

  const handleEdit = item => {
    setIsEditMode(true); setEditingItem(item);
    setFormData({ name: item.name || '', description: item.description || '', checkListQuery: item.checkListQuery || '' });
    setFormErrors({}); onOpen();
  };

  const handleView = item => { setViewingItem(item); onViewOpen(); };

  // ─── Delete ───────────────────────────────────────────────────────────────
  const handleDelete  = item => { setItemToDelete(item); setDeleteModalOpen(true); };
  const cancelDelete  = () => { setDeleteModalOpen(false); setItemToDelete(null); };
  const confirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      const res = await apiService.deleteChecklist(itemToDelete.id);
      if (res.success) {
          usageTracker.action('Checklist', 'DELETE');
        handleRefresh();
        toast({ title: 'Deleted', description: `"${itemToDelete.name}" deleted.`, status: 'success', duration: 3000, isClosable: true });
        cancelDelete();
      } else throw new Error('Failed to delete');
    } catch {
      toast({ title: 'Error', description: 'Failed to delete checklist.', status: 'error', duration: 3000, isClosable: true });
    }
  };

  // ─── Filter & Paginate ────────────────────────────────────────────────────
  const filtered = checklists.filter(c => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (c.name || '').toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q);
  });

  const itemsPerPage = parseInt(pageSize.split(' ')[0]);
  const totalPages   = Math.ceil(filtered.length / itemsPerPage);
  const startIndex   = (currentPage - 1) * itemsPerPage;
  const paginated    = filtered.slice(startIndex, startIndex + itemsPerPage);

  const formatDateTime = d => {
    if (!d) return 'N/A';
    const date = new Date(d.replace(' ', 'T').split('.')[0] + 'Z');
    return isNaN(date) ? 'Invalid Date' : date.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  };

  const getTimeAgo = d => {
    if (!d) return 'Never';
    const past = new Date(d.replace(' ', 'T').split('.')[0] + 'Z');
    if (isNaN(past)) return 'Invalid Date';
    const mins = Math.floor((Date.now() - past) / 60000);
    if (mins < 1)   return 'Just now';
    if (mins < 60)  return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)   return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days === 1) return 'Yesterday';
    if (days < 7)   return `${days}d ago`;
    const wks = Math.floor(days / 7);
    if (wks < 4)    return `${wks}w ago`;
    const mos = Math.floor(days / 30);
    if (mos < 12)   return `${mos}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  };

  const handleCheckListNameClick= useCallback((id) => {
          try {
              const params = new URLSearchParams();
              params.set('checklistId', id);
              const mappingsPath = `/checklist-mappings?${params.toString()}`
              //const issuesPath = `/issues?${params.toString()}`;
              navigate(mappingsPath);
          } catch (error) {
              console.error('Failed to navigate to Client Profile:', error);
              
          }
      }, [navigate]);

  // ─── Loading / Error screens ──────────────────────────────────────────────
  if (loading) return (
    <Box bg={bgColor} minH="100vh" p={8}>
      <Center py={20}>
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text color={textColor} fontSize="lg">Loading checklists...</Text>
        </VStack>
      </Center>
    </Box>
  );

  if (error) return (
    <Box bg={bgColor} minH="100vh" p={8}>
      <Alert status="error" bg={cardBg} borderColor="red.300" borderWidth="1px" borderRadius="md">
        <AlertIcon />
        <Box flex="1">
          <AlertTitle color="red.600">Error!</AlertTitle>
          <AlertDescription color={textColor}>{error}</AlertDescription>
        </Box>
        <Button size="sm" colorScheme="red" variant="outline" onClick={handleRefresh}>Retry</Button>
      </Alert>
    </Box>
  );

  return (
    <Box bg={bgColor} minH="100vh" p={8}>

      {/* ── Header ── */}
      <Flex justify="space-between" align="flex-start" mb={6}>
        <Box>
          <Text fontSize="2xl" fontWeight="bold" color={headingColor} mb={1}>Checklist Manager</Text>
          <Text color={textColor} fontSize="sm">Manage SQL-based checklists for monitoring and validation</Text>
        </Box>
        <HStack spacing={3}>
          <Button leftIcon={<MdRefresh />} variant="outline" size="sm" borderColor={borderColor} color={textColor} onClick={handleRefresh} _hover={{ bg: hoverBg, borderColor: btnHoverBorder }}>Refresh</Button>
          <Button leftIcon={<MdAdd />} colorScheme="blue" size="sm" onClick={handleCreateNew} _hover={{ bg: blueHover }}>Add Checklist</Button>
        </HStack>
      </Flex>

      {/* ── Stats ── */}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={6}>
        <Card bg={statsCardBg} boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
          <CardBody><Stat><StatLabel color={textColor} fontSize="sm">Total Checklists</StatLabel><StatNumber fontSize="2xl" fontWeight="bold" color={headingColor}>{stats.total}</StatNumber></Stat></CardBody>
        </Card>
        <Card bg={statsCardBg} boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
          <CardBody><Stat><StatLabel color={textColor} fontSize="sm">With SQL Query</StatLabel><StatNumber fontSize="2xl" fontWeight="bold" color="blue.500">{stats.withQuery}</StatNumber></Stat></CardBody>
        </Card>
        <Card bg={statsCardBg} boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
          <CardBody><Stat><StatLabel color={textColor} fontSize="sm">Updated This Week</StatLabel><StatNumber fontSize="2xl" fontWeight="bold" color="green.500">{stats.recent}</StatNumber></Stat></CardBody>
        </Card>
      </SimpleGrid>

      {/* ── Filters ── */}
      <Card bg={cardBg} boxShadow="sm" mb={6} borderWidth="1px" borderColor={borderColor}>
        <CardBody>
          <Flex gap={4} align="center">
            <Box flex="1">
              <InputGroup>
                <InputLeftElement pointerEvents="none"><MdSearch color={searchIconColor} /></InputLeftElement>
                <Input
                  placeholder="Search by name or description"
                  value={searchTerm}
                  onChange={e => handleSearch(e.target.value)}
                  bg={inputBg} borderColor={borderColor} color={headingColor}
                  _placeholder={{ color: mutedTextColor }}
                />
              </InputGroup>
            </Box>
            <FormControl maxW="180px">
              <Select
                size="md" value={pageSize}
                onChange={e => { setPageSize(e.target.value); setCurrentPage(1); }}
                bg={inputBg} borderColor={borderColor} color={headingColor}
              >
                <option value="20 per page">20 per page</option>
                <option value="50 per page">50 per page</option>
                <option value="100 per page">100 per page</option>
              </Select>
            </FormControl>
            <Button
              variant="outline" colorScheme="purple" size="md" onClick={resetFilters}
              borderColor={purpleBorder} color={purpleText}
              _hover={{ bg: purpleHoverBg, borderColor: purpleHoverBorder }}
            >Reset Filters</Button>
          </Flex>
        </CardBody>
      </Card>

      {/* ── Table ── */}
      <Card bg={cardBg} boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
        <CardBody p={0}>
          <Flex justify="space-between" align="center" p={4} borderBottom="1px" borderBottomColor={borderColor}>
            <Text fontWeight="semibold" fontSize="md" color={headingColor}>Checklists ({filtered.length})</Text>
            <HStack spacing={4} fontSize="sm" color={textColor}>
              <Text>PAGE {currentPage} OF {totalPages || 1}</Text>
              <Text>SHOWING {Math.min(itemsPerPage, filtered.length - startIndex)} OF {filtered.length}</Text>
            </HStack>
          </Flex>

          {filtered.length === 0 ? (
            <Center py={14}>
              <VStack spacing={3}>
                <Box color={mutedTextColor}><TbChecklist size={40} /></Box>
                <Text color={mutedTextColor} fontSize="lg">No checklists found</Text>
                <Text color={mutedTextColor} fontSize="sm">{searchTerm ? 'Try adjusting your search' : 'Add your first checklist to get started'}</Text>
              </VStack>
            </Center>
          ) : (
            <>
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead bg={tableHeaderBg} borderBottom="2px" borderColor={borderColor}>
                    <Tr>
                      {['Name', 'Description', 'Last Updated', 'Actions'].map(h => (
                        <Th key={h} color={headingColor} fontWeight="semibold" fontSize="xs" textTransform="uppercase" letterSpacing="wider" py={4}>{h}</Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {paginated.map((item, idx) => (
                      <Tr key={`${item.id}-${idx}`} _hover={{ bg: hoverBg }}>

                        {/* Name */}
                        <Td py={4} borderColor={borderColor} maxW="220px"
                            onClick={()=>handleCheckListNameClick(item.id)}>
                          <div style={{cursor: "pointer"}}>
                            <VStack align="start" spacing={1}>
                            <Text fontSize="sm" fontWeight="semibold" color={headingColor} isTruncated maxW="200px">{item.name}</Text>
                            <HStack spacing={2}>
                              <Tag size="sm" bg={tagSqlBg} borderRadius="sm">
                                <TagLabel fontSize="10px" color={tagSqlColor} fontWeight="bold">
                                  <HStack spacing={1}><MdCode size={10} /><Text>SQL</Text></HStack>
                                </TagLabel>
                              </Tag>
                              <Text fontSize="xs" color={mutedTextColor}>ID: {item.id}</Text>
                            </HStack>
                          </VStack>
                          </div>
                        </Td>

                        {/* Description */}
                        <Td py={4} borderColor={borderColor} maxW="360px">
                          <Tooltip label={item.description} placement="top" isDisabled={!item.description || item.description.length <= 90}>
                            <Text fontSize="sm" color={textColor} noOfLines={2}>
                              {item.description || <Text as="span" color={mutedTextColor} fontStyle="italic">No description</Text>}
                            </Text>
                          </Tooltip>
                        </Td>

                        {/* Last Updated */}
                        <Td py={4} borderColor={borderColor} minW="160px">
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm" color={headingColor} fontWeight="medium">{getTimeAgo(item.updated_at)}</Text>
                            <Text fontSize="xs" color={mutedTextColor}>{formatDateTime(item.updated_at)}</Text>
                          </VStack>
                        </Td>

                        {/* Actions */}
                        <Td py={4} borderColor={borderColor}>
                          <Menu>
                            <MenuButton as={IconButton} icon={<MdMoreVert />} variant="ghost" size="sm" color={textColor} _hover={{ bg: hoverBg, color: headingColor }} />
                            <MenuList bg={cardBg} borderColor={borderColor} boxShadow={menuShadow}>
                              <MenuItem icon={<MdVisibility />} onClick={() => handleView(item)}   bg={cardBg} color={headingColor} _hover={{ bg: hoverBg }}>View</MenuItem>
                              <MenuItem icon={<MdEdit />}       onClick={() => handleEdit(item)}   bg={cardBg} color={headingColor} _hover={{ bg: hoverBg }}>Edit</MenuItem>
                              <MenuItem icon={<MdDelete />}     onClick={() => handleDelete(item)} bg={cardBg} color="red.500"      _hover={{ bg: redHoverBg }}>Delete</MenuItem>
                            </MenuList>
                          </Menu>
                        </Td>

                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {totalPages > 1 && (
                <Flex justify="space-between" align="center" p={4} borderTop="1px" borderTopColor={borderColor}>
                  <Text fontSize="sm" color={textColor}>
                    Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filtered.length)} of {filtered.length} entries
                  </Text>
                  <HStack spacing={2}>
                    {['First', 'Previous'].map(lbl => (
                      <Button key={lbl} size="sm"
                        onClick={() => setCurrentPage(lbl === 'First' ? 1 : currentPage - 1)}
                        isDisabled={currentPage === 1}
                        variant="outline" borderColor={borderColor} color={textColor} _hover={{ bg: hoverBg }}
                      >{lbl}</Button>
                    ))}
                    {(() => {
                      const pages = [], maxV = 5;
                      let s = Math.max(1, currentPage - 2), e = Math.min(totalPages, s + maxV - 1);
                      if (e - s < maxV - 1) s = Math.max(1, e - maxV + 1);
                      for (let i = s; i <= e; i++) pages.push(
                        <Button key={i} size="sm"
                          variant={i === currentPage ? 'solid' : 'outline'}
                          colorScheme={i === currentPage ? 'blue' : 'gray'}
                          onClick={() => setCurrentPage(i)}
                          borderColor={i === currentPage ? 'blue.500' : borderColor}
                          color={i === currentPage ? 'white' : textColor}
                          bg={i === currentPage ? 'blue.500' : 'transparent'}
                          _hover={{ bg: i === currentPage ? 'blue.600' : hoverBg }}
                        >{i}</Button>
                      );
                      return pages;
                    })()}
                    {['Next', 'Last'].map(lbl => (
                      <Button key={lbl} size="sm"
                        onClick={() => setCurrentPage(lbl === 'Last' ? totalPages : currentPage + 1)}
                        isDisabled={currentPage === totalPages}
                        variant="outline" borderColor={borderColor} color={textColor} _hover={{ bg: hoverBg }}
                      >{lbl}</Button>
                    ))}
                  </HStack>
                </Flex>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {/* ── Add / Edit Modal ── */}
      <Modal isOpen={isOpen} onClose={handleModalClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay bg={modalOverlay} />
        <ModalContent bg={cardBg} borderColor={borderColor} borderWidth="1px" maxH="90vh">
          <ModalHeader color={headingColor} borderBottom="1px" borderBottomColor={borderColor} pb={4}>
            <HStack spacing={3}>
              <Box color="blue.500"><TbChecklist size={22} /></Box>
              <VStack align="start" spacing={0}>
                <Text>{isEditMode ? 'Edit Checklist' : 'Add New Checklist'}</Text>
                {isEditMode && <Text fontSize="xs" color={mutedTextColor} fontWeight="normal">ID: {editingItem?.id}</Text>}
              </VStack>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color={textColor} />

          <ModalBody py={6} overflowY="auto">
            <VStack spacing={5} align="stretch">

              {/* Name */}
              <FormControl isRequired isInvalid={!!formErrors.name}>
                <FormLabel color={headingColor} fontSize="sm" fontWeight="medium">Checklist Name</FormLabel>
                <Input
                  placeholder="e.g. Daily Data Integrity Check"
                  value={formData.name}
                  onChange={e => { setFormData({ ...formData, name: e.target.value }); setFormErrors({ ...formErrors, name: '' }); }}
                  bg={inputBg} borderColor={borderColor} color={headingColor}
                  _placeholder={{ color: mutedTextColor }}
                  _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)' }}
                />
                {formErrors.name && <Text fontSize="xs" color="red.500" mt={1}>{formErrors.name}</Text>}
              </FormControl>

              {/* Description */}
              <FormControl>
                <FormLabel color={headingColor} fontSize="sm" fontWeight="medium">
                  Description <Text as="span" color={mutedTextColor} fontWeight="normal">(optional)</Text>
                </FormLabel>
                <Textarea
                  placeholder="Brief description of what this checklist validates..."
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  bg={inputBg} borderColor={borderColor} color={headingColor} rows={3} resize="vertical"
                  _placeholder={{ color: mutedTextColor }}
                  _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)' }}
                />
              </FormControl>

              {/* SQL Query */}
              <FormControl isRequired isInvalid={!!formErrors.checkListQuery}>
                <FormLabel color={headingColor} fontSize="sm" fontWeight="medium">
                  <HStack spacing={2}><MdCode size={16} /><Text>Checklist SQL Query</Text></HStack>
                </FormLabel>
                <SqlEditor
                  value={formData.checkListQuery}
                  onChange={val => {
                    setFormData({ ...formData, checkListQuery: val });
                    setFormErrors({ ...formErrors, checkListQuery: '' });
                  }}
                  height="320px"
                  tokens={sqlTokens}
                  formError={formErrors.checkListQuery}
                  toast={toast}
                />
                {formErrors.checkListQuery && <Text fontSize="xs" color="red.500" mt={1}>{formErrors.checkListQuery}</Text>}
                <Text fontSize="xs" color={mutedTextColor} mt={1.5}>
                  Paste or write your SQL query. Supports large queries — the editor is fully scrollable.
                </Text>
              </FormControl>

            </VStack>
          </ModalBody>

          <ModalFooter borderTop="1px" borderTopColor={borderColor}>
            <Button colorScheme="blue" mr={3} onClick={handleSave} _hover={{ bg: blueHover }}>
              {isEditMode ? 'Update Checklist' : 'Add Checklist'}
            </Button>
            <Button variant="ghost" onClick={handleModalClose} color={textColor} _hover={{ bg: hoverBg, color: headingColor }}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── View Modal ── */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay bg={modalOverlay} />
        <ModalContent bg={cardBg} borderColor={borderColor} borderWidth="1px" maxH="90vh">
          <ModalHeader color={headingColor} borderBottom="1px" borderBottomColor={borderColor} pb={4}>
            <HStack spacing={3}>
              <Box color="green.500"><MdVisibility size={22} /></Box>
              <VStack align="start" spacing={0}>
                <Text fontSize="lg">{viewingItem?.name}</Text>
                <Text fontSize="xs" color={mutedTextColor} fontWeight="normal">ID: {viewingItem?.id}</Text>
              </VStack>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color={textColor} />

          <ModalBody py={6} overflowY="auto">
            <VStack spacing={5} align="stretch">

              {/* Description */}
              <Box>
                <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor} textTransform="uppercase" letterSpacing="wider" mb={2}>Description</Text>
                <Box px={4} py={3} bg={descBg} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                  <Text
                    fontSize="sm"
                    color={viewingItem?.description ? headingColor : mutedTextColor}
                    fontStyle={viewingItem?.description ? 'normal' : 'italic'}
                  >
                    {viewingItem?.description || 'No description provided.'}
                  </Text>
                </Box>
              </Box>

              {/* Meta */}
              <HStack spacing={8} bg={metaBg} borderRadius="md" borderWidth="1px" borderColor={borderColor} px={4} py={3}>
                <Box>
                  <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor} textTransform="uppercase" letterSpacing="wider" mb={1}>Last Updated</Text>
                  <Text fontSize="sm" color={headingColor} fontWeight="medium">{formatDateTime(viewingItem?.updated_at)}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor} textTransform="uppercase" letterSpacing="wider" mb={1}>Time Ago</Text>
                  <Text fontSize="sm" color={headingColor} fontWeight="medium">{getTimeAgo(viewingItem?.updated_at)}</Text>
                </Box>
              </HStack>

              {/* SQL Query */}
              <Box>
                <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor} textTransform="uppercase" letterSpacing="wider" mb={2}>SQL Query</Text>
                <SqlEditor
                  value={viewingItem?.checkListQuery || ''}
                  isReadOnly
                  height="360px"
                  tokens={sqlTokens}
                  toast={toast}
                />
              </Box>

            </VStack>
          </ModalBody>

          <ModalFooter borderTop="1px" borderTopColor={borderColor}>
            <Button colorScheme="blue" mr={3} leftIcon={<MdEdit />} onClick={() => { onViewClose(); handleEdit(viewingItem); }} _hover={{ bg: blueHover }}>Edit</Button>
            <Button variant="ghost" onClick={onViewClose} color={textColor} _hover={{ bg: hoverBg, color: headingColor }}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Delete Modal ── */}
      <Modal isOpen={deleteModalOpen} onClose={cancelDelete} size="md" isCentered>
        <ModalOverlay bg={modalOverlay} />
        <ModalContent bg={cardBg} borderColor={borderColor} borderWidth="1px">
          <ModalHeader color={headingColor} borderBottom="1px" borderBottomColor={borderColor}>
            <HStack spacing={3}><Box color="red.500"><MdDelete size={24} /></Box><Text>Confirm Deletion</Text></HStack>
          </ModalHeader>
          <ModalCloseButton color={textColor} />
          <ModalBody py={6}>
            <VStack spacing={4} align="stretch">
              <Alert status="warning" variant="left-accent" borderRadius="md">
                <AlertIcon />
                <Box><AlertTitle fontSize="sm">Warning</AlertTitle><AlertDescription fontSize="sm">This action cannot be undone.</AlertDescription></Box>
              </Alert>
              <Text color={textColor} fontSize="sm">Are you sure you want to delete the following checklist?</Text>
              <Card bg={hoverBg} borderWidth="1px" borderColor={borderColor}>
                <CardBody p={3}>
                  <VStack align="stretch" spacing={2}>
                    <HStack justify="space-between">
                      <Text fontSize="xs" color={mutedTextColor}>Name:</Text>
                      <Text fontSize="sm" color={headingColor} fontWeight="medium">{itemToDelete?.name}</Text>
                    </HStack>
                    <HStack justify="space-between" align="start">
                      <Text fontSize="xs" color={mutedTextColor} flexShrink={0}>Description:</Text>
                      <Text fontSize="sm" color={textColor} textAlign="right" maxW="260px" noOfLines={2}>{itemToDelete?.description || '—'}</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="xs" color={mutedTextColor}>ID:</Text>
                      <Text fontSize="sm" color={textColor}>{itemToDelete?.id}</Text>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>
            </VStack>
          </ModalBody>
          <ModalFooter borderTop="1px" borderTopColor={borderColor}>
            <Button colorScheme="red" mr={3} onClick={confirmDelete} leftIcon={<MdDelete />}>Delete Checklist</Button>
            <Button variant="ghost" onClick={cancelDelete} color={textColor} _hover={{ bg: hoverBg, color: headingColor }}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </Box>
  );
};

export default ChecklistManager;